<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
// Falls back to the global avatar, so this is correct with the feature off too.
const currentAccountAvatarUrl = useMapGetter('getCurrentAccountAvatarUrl');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const canAccessProfileSettings = computed(() => {
  const resolvedRoute = router.resolve(
    accountScopedRoute('profile_settings_index')
  );
  return shouldShow(
    resolvedRoute?.meta?.featureFlag || null,
    resolvedRoute?.meta?.permissions || [],
    resolvedRoute?.meta?.installationTypes || []
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: canAccessProfileSettings.value,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: false,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: '/help_doc/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://wlab.biz/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});

const currentTheme = ref('mix');

const themeOptions = [
  { key: 'light', label: 'Light', icon: 'i-ph-sun-bold' },
  { key: 'dark', label: 'Dark', icon: 'i-ph-moon-bold' },
  { key: 'mix', label: 'Mix', icon: 'i-ph-squares-four-bold' },
  { key: 'auto', label: 'Auto', icon: 'i-ph-desktop-bold' },
];

const loadTheme = () => {
  const savedTheme = LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'mix';
  currentTheme.value = savedTheme;
};

const setTheme = theme => {
  currentTheme.value = theme;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);
};

onMounted(() => {
  loadTheme();
});
</script>

<template>
  <DropdownContainer
    class="relative w-full min-w-0"
    :ignore="['.js-nested-dropdown']"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentAccountAvatarUrl"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div class="min-w-0">
          <div
            class="text-sm font-medium leading-4 truncate text-n-slate-12 dark:text-white/90"
          >
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11 dark:text-white/60">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody
      class="bottom-full z-50 mb-2 w-80 ltr:left-0 rtl:right-0 wlab-profile-menu"
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <!-- Theme Toggle -->
      <div class="wlab-pm-theme-row">
        <div class="wlab-pm-theme-label">
          <span class="i-ph-paint-brush-bold size-4" />
          <span>{{ t('SIDEBAR_ITEMS.THEME_MODE') }}</span>
        </div>
        <div class="wlab-pm-theme-pills">
          <button
            v-for="option in themeOptions"
            :key="option.key"
            class="wlab-pm-theme-pill"
            :class="{ 'is-active': currentTheme === option.key }"
            :title="option.label"
            @click="setTheme(option.key)"
          >
            <span class="size-4" :class="[option.icon]" />
          </button>
        </div>
      </div>
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>

<style scoped>
.wlab-profile-menu {
  --pm-bg: #ffffff;
  --pm-border: rgba(15, 23, 42, 0.12);
  --pm-shadow: 0 18px 48px -14px rgba(15, 23, 42, 0.28),
    0 8px 18px -10px rgba(15, 23, 42, 0.16);
  --pm-item-hover: rgba(15, 23, 42, 0.055);
  --pm-text: #0f172a;
  --pm-text-secondary: #667085;
  --pm-divider: rgba(15, 23, 42, 0.1);
  --pm-pill-bg: rgba(15, 23, 42, 0.05);
  --pm-pill-border: rgba(15, 23, 42, 0.08);
  --pm-pill-hover: rgba(15, 23, 42, 0.095);
  --pm-status-chip-bg: #f3f4f6;
  --pm-status-chip-border: #e5e7eb;
  --pm-active-pill-bg: #0066ff;
  --pm-active-pill-shadow: 0 4px 10px rgba(0, 102, 255, 0.35);
}

:global(body.dark:not(.theme-mix)) .wlab-profile-menu,
:global(html.dark body:not(.theme-mix)) .wlab-profile-menu {
  --pm-bg: #151922;
  --pm-border: rgba(255, 255, 255, 0.1);
  --pm-shadow: 0 24px 58px -16px rgba(0, 0, 0, 0.62),
    0 10px 24px -12px rgba(0, 0, 0, 0.44);
  --pm-item-hover: rgba(255, 255, 255, 0.075);
  --pm-text: #f8fafc;
  --pm-text-secondary: #9aa3b2;
  --pm-divider: rgba(255, 255, 255, 0.1);
  --pm-pill-bg: rgba(255, 255, 255, 0.06);
  --pm-pill-border: rgba(255, 255, 255, 0.1);
  --pm-pill-hover: rgba(255, 255, 255, 0.12);
  --pm-status-chip-bg: rgba(255, 255, 255, 0.08);
  --pm-status-chip-border: rgba(255, 255, 255, 0.12);
  --pm-active-pill-bg: #1f7aff;
  --pm-active-pill-shadow: 0 6px 14px rgba(31, 122, 255, 0.45);
}

.wlab-profile-menu :deep(.n-dropdown-body) {
  background: var(--pm-bg) !important;
  border: 1px solid var(--pm-border) !important;
  border-radius: 18px !important;
  box-shadow: var(--pm-shadow) !important;
  backdrop-filter: none !important;
  padding: 8px !important;
  gap: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  min-width: 332px !important;
}

.wlab-profile-menu :deep(.n-dropdown-item > *) {
  border-radius: 10px !important;
  padding: 9px 10px !important;
  min-height: 40px;
  font-size: 13px !important;
  color: var(--pm-text) !important;
  font-weight: 500;
  transition:
    background-color 0.12s ease,
    color 0.12s ease;
}

.wlab-profile-menu :deep(.wlab-pm-status-title) {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  color: var(--pm-text) !important;
}

.wlab-profile-menu :deep(.n-dropdown-item > *:hover) {
  background: var(--pm-item-hover) !important;
}

.wlab-profile-menu :deep(.n-dropdown-item .size-4) {
  color: var(--pm-text-secondary) !important;
}

.wlab-profile-menu :deep(.border-n-strong) {
  border-color: var(--pm-divider) !important;
  margin: 6px 0 !important;
}

.wlab-profile-menu :deep(.js-nested-dropdown .n-dropdown-body) {
  background: var(--pm-bg) !important;
  border: 1px solid var(--pm-border) !important;
  border-radius: 12px !important;
  box-shadow: var(--pm-shadow) !important;
  backdrop-filter: none !important;
  padding: 4px !important;
  gap: 1px !important;
}

.wlab-profile-menu :deep(.wlab-profile-menu-status) {
  padding: 2px 2px 4px;
}

.wlab-profile-menu :deep(.wlab-profile-menu-status .n-dropdown-item > *) {
  min-height: 44px;
  border-radius: 11px !important;
  padding: 9px 10px !important;
}

.wlab-profile-menu :deep(.wlab-pm-availability-btn) {
  min-width: 114px !important;
  height: 36px !important;
  border-radius: 12px !important;
  border: 1px solid var(--pm-status-chip-border) !important;
  background: var(--pm-status-chip-bg) !important;
  color: var(--pm-text) !important;
  box-shadow: none !important;
}

.wlab-profile-menu :deep(.wlab-pm-availability-btn:hover) {
  background: var(--pm-item-hover) !important;
}

.wlab-profile-menu :deep(.wlab-pm-availability-btn .size-4),
.wlab-profile-menu :deep(.wlab-pm-availability-btn svg),
.wlab-profile-menu :deep(.wlab-pm-availability-btn span) {
  color: var(--pm-text) !important;
}

.wlab-profile-menu
  :deep(.wlab-profile-menu-status .n-dropdown-item .i-lucide-info) {
  color: var(--pm-text-secondary) !important;
}

.wlab-pm-theme-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
}

.wlab-pm-theme-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 400;
  color: var(--pm-text);
}
.wlab-pm-theme-label .size-4 {
  color: var(--pm-text-secondary);
}

.wlab-pm-theme-pills {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--pm-pill-bg);
  border-radius: 10px;
  border: 1px solid var(--pm-pill-border);
}

.wlab-pm-theme-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 26px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: var(--pm-text-secondary);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.wlab-pm-theme-pill:hover {
  background: var(--pm-pill-hover);
  color: var(--pm-text);
}

.wlab-pm-theme-pill.is-active {
  background: var(--pm-active-pill-bg);
  color: white;
  box-shadow: var(--pm-active-pill-shadow);
}
</style>

<style>
body.theme-mix .wlab-profile-menu .wlab-pm-availability-btn,
body.theme-mix .wlab-profile-menu .wlab-pm-availability-btn *,
body.theme-mix .wlab-profile-menu .wlab-pm-availability-btn span {
  color: #000000 !important;
}
</style>
